import { gaEvent } from './ga';
import { readAttribution } from './attribution';

/**
 * GA4 funnel ("flow") instrumentation — the Apply half of the cross-app
 * contract shared with Volt (`volt-portal/src/lib/gaFlow.ts`, the reference
 * implementation — keep the vocabulary and the classifier lists in sync) and
 * the marketing site (`cta_click` in BaseLayout.astro). Registry, origin
 * model and the GA4 exploration setup: `docs-tento/marketing/ga4-flow-funnels.md`.
 *
 * Events: flow_start (first step of a flow seen this session), flow_step
 * (a step shown), flow_step_complete (its primary action succeeded),
 * flow_error (it failed: api | validation | auth), flow_complete (terminal
 * outcome — a key event; `flow_outcome` says which).
 *
 * Every event carries flow_id / flow_step / flow_step_index, `flow_variant`
 * (the brand host: apply, woo, triton …), and three origin layers so a funnel
 * can be broken down on any step: attr_* (first-touch `tento_attr` cookie:
 * utm source/medium/campaign/content, landing page, classified referrer),
 * flow_entry (+ flow_entry_page: how THIS session reached Apply). No ids,
 * no PII, low-cardinality slugs only.
 *
 * The HTML shell pushes gtag `config` at parse time and only defers the
 * library fetch to human evidence, so events fired on mount sit in the
 * dataLayer after `config` and are sent in order once the tag loads — or go
 * nowhere on staging / for staff / automation.
 */

export const FLOW_STEPS = {
  apply_financing: {
    landing: 0,
    amount: 1,
    info: 2,
    auth: 3,
    business_more_details: 4,
    business_further_details: 5,
    type_of_funding: 6,
    owners: 7,
    bank_connect: 8,
    summary: 9,
    decision: 10,
    approved_next_steps: 11,
    personal_loan: 11,
    personal_loan_offers: 12,
  },
  apply_equipment: {
    not_ready: 0,
    landing: 0,
    your_information: 1,
    product_details: 2,
    your_business: 3,
    owners: 4,
    summary: 5,
    decision: 6,
    approved_next_steps: 7,
    personal_loan: 7,
  },
  apply_bcm: {
    landing: 0,
    info: 1,
    auth: 2,
    business_details: 3,
    summary: 4,
    // Optional post-submit card step (billed cohort, flag-gated). Shares
    // the terminal index — signup itself completed at business_details.
    // Mirrors volt_bcs_signup's add_card convention.
    add_card: 5,
    add_card_skipped: 5,
    done: 5,
  },
} as const;

export type FlowId = keyof typeof FLOW_STEPS;
export type FlowStep<F extends FlowId> = keyof (typeof FLOW_STEPS)[F] & string;
export type FlowOutcome =
  | 'bcs_signup_submitted'
  | 'financing_submitted'
  | 'financing_approved'
  | 'financing_declined'
  | 'financing_frozen'
  | 'financing_review'
  | 'personal_loan_recommended';
export type FlowErrorType = 'api' | 'validation' | 'auth';
export type FlowParams = Record<string, string | number | boolean | null | undefined>;

const NONE = '(none)';
const STARTED_KEY = 'tento_ga_flow_started';
const COMPLETED_KEY = 'tento_ga_flow_completed';
const ENTRY_KEY = 'tento_ga_entry';

/* ------------------------------------------------------------------------ */
/* Route -> (flow, step). The wizard navigates with raw pushState, so this   */
/* is driven from ApplyWizardContext, not the router.                        */
/* ------------------------------------------------------------------------ */

export type FlowLocation = { flow: FlowId; step: string; variant?: string };

const strip = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

/** Map an Apply pathname to its funnel position, or null for non-funnel routes. */
export const flowForPath = (pathname: string): FlowLocation | null => {
  const p = strip(pathname || '/');
  // Equipment lead flow
  if (p === '/embed/learn-more') return { flow: 'apply_equipment', step: 'not_ready' };
  if (p === '/eq') return { flow: 'apply_equipment', step: 'landing' };
  if (p === '/eq/your-information') return { flow: 'apply_equipment', step: 'your_information' };
  if (p === '/eq/product-details') return { flow: 'apply_equipment', step: 'product_details' };
  if (p === '/eq/your-business') return { flow: 'apply_equipment', step: 'your_business' };
  if (p === '/eq/owners') return { flow: 'apply_equipment', step: 'owners' };
  if (p === '/eq/summary') return { flow: 'apply_equipment', step: 'summary' };
  if (p === '/eq/decision/personal') return { flow: 'apply_equipment', step: 'personal_loan' };
  if (p === '/eq/decision/success' || p === '/eq/app/contact-soon')
    return { flow: 'apply_equipment', step: 'approved_next_steps' };
  if (p.startsWith('/eq/decision')) return { flow: 'apply_equipment', step: 'decision' };
  // BCM short (live) and legacy /bcm2
  const bcm = p.match(/^\/(bcm2?)(\/.*)?$/);
  if (bcm) {
    const variant = bcm[1] === 'bcm2' ? 'legacy' : undefined;
    const rest = bcm[2] || '';
    if (rest === '') return { flow: 'apply_bcm', step: 'landing', variant };
    if (rest === '/app/info') return { flow: 'apply_bcm', step: 'info', variant };
    if (rest === '/app/create' || rest === '/business/select') return { flow: 'apply_bcm', step: 'auth', variant };
    if (rest === '/app/business/more-details') return { flow: 'apply_bcm', step: 'business_details', variant };
    if (rest === '/app/summary') return { flow: 'apply_bcm', step: variant ? 'summary' : 'done', variant };
    if (rest === '/app/add-card') return { flow: 'apply_bcm', step: 'add_card', variant };
    if (rest === '/app/contact-soon') return { flow: 'apply_bcm', step: 'done', variant };
    return null;
  }
  // Standard financing
  if (p === '/') return { flow: 'apply_financing', step: 'landing' };
  if (p === '/app/amount') return { flow: 'apply_financing', step: 'amount' };
  if (p === '/app/info') return { flow: 'apply_financing', step: 'info' };
  if (p === '/login' || p === '/app/verify/login' || p === '/app/create' || p === '/app/select')
    return { flow: 'apply_financing', step: 'auth' };
  if (p === '/app/business/more-details') return { flow: 'apply_financing', step: 'business_more_details' };
  if (p === '/app/business/further-details') return { flow: 'apply_financing', step: 'business_further_details' };
  if (p === '/app/type-of-funding') return { flow: 'apply_financing', step: 'type_of_funding' };
  if (p === '/app/business/owners') return { flow: 'apply_financing', step: 'owners' };
  if (p === '/app/bank-connect') return { flow: 'apply_financing', step: 'bank_connect' };
  if (p === '/app/funding-is-almost-here') return { flow: 'apply_financing', step: 'summary' };
  if (p === '/app/decision/personal') return { flow: 'apply_financing', step: 'personal_loan' };
  if (p === '/app/decision/offers') return { flow: 'apply_financing', step: 'personal_loan_offers' };
  if (p.startsWith('/app/decision')) return { flow: 'apply_financing', step: 'decision' };
  if (p === '/app/contact-soon' || p.startsWith('/app/agreement') || p === '/app/done')
    return { flow: 'apply_financing', step: 'approved_next_steps' };
  return null;
};

/** Backend decision code -> funnel outcome (same buckets as Volt's mapDecisionToOutcome). */
export const outcomeForDecision = (decision: string | null | undefined): FlowOutcome => {
  const d = (decision || '').toLowerCase();
  if (d === 'proceed' || /\.proceed\.(wc|eq)$/.test(d)) return 'financing_approved';
  if (d === 'offers' || /\.proceed\.personal$/.test(d)) return 'personal_loan_recommended';
  if (/frozen$/.test(d)) return 'financing_frozen';
  // business.review.* (e.g. credit_check_error) — manual review, not a decline.
  if (/\.review\./.test(d)) return 'financing_review';
  return 'financing_declined';
};

/* ------------------------------------------------------------------------ */
/* Origin classification (mirror of volt-portal lib/gaFlow.ts)               */
/* ------------------------------------------------------------------------ */

const TENTO_HOST_RE = /(^|\.)tento\.(com|co)$/;

const REFERRER_CLASSES: Array<[RegExp, string]> = [
  [/(^|\.)(chatgpt\.com|chat\.openai\.com|openai\.com)$/, 'ai:chatgpt'],
  [/(^|\.)perplexity\.ai$/, 'ai:perplexity'],
  [/(^|\.)(claude\.ai|anthropic\.com)$/, 'ai:claude'],
  [/^(gemini|bard)\.google\.com$/, 'ai:gemini'],
  [/(^|\.)copilot\.microsoft\.com$/, 'ai:copilot'],
  [
    /(^|\.)(you\.com|phind\.com|poe\.com|meta\.ai|mistral\.ai|grok\.com|x\.ai|deepseek\.com|kimi\.com|duck\.ai)$/,
    'ai:other',
  ],
  [/^mail\.google\.com$/, 'email:gmail'],
  [
    /(^|\.)(outlook\.(live|office|office365)\.com|mail\.yahoo\.com|mail\.aol\.com|mail\.proton\.me|protonmail\.com)$/,
    'email:webmail',
  ],
  [/(^|\.)google\.[a-z.]+$/, 'search:google'],
  [/(^|\.)bing\.com$/, 'search:bing'],
  [/(^|\.)duckduckgo\.com$/, 'search:duckduckgo'],
  [/(^|\.)yahoo\.[a-z.]+$/, 'search:yahoo'],
  [
    /(^|\.)(ecosia\.org|search\.brave\.com|yandex\.[a-z]+|baidu\.com|startpage\.com|qwant\.com|ask\.com|aol\.com)$/,
    'search:other',
  ],
  [/(^|\.)(facebook\.com|fb\.com|messenger\.com)$/, 'social:facebook'],
  [/(^|\.)instagram\.com$/, 'social:instagram'],
  [/(^|\.)(linkedin\.com|lnkd\.in)$/, 'social:linkedin'],
  [/(^|\.)(twitter\.com|x\.com|t\.co)$/, 'social:x'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'social:youtube'],
  [/(^|\.)(tiktok\.com|reddit\.com|pinterest\.com|threads\.net|snapchat\.com|nextdoor\.com)$/, 'social:other'],
];

export const classifyReferrer = (referrer: string | null | undefined, ownHost?: string): string => {
  if (!referrer) return 'direct';
  let url: URL;
  try {
    url = new URL(referrer);
  } catch (e) {
    return 'direct';
  }
  const host = url.hostname.toLowerCase();
  if (ownHost && host === ownHost.toLowerCase()) return 'internal';
  if (TENTO_HOST_RE.test(host))
    return `tento:${host}${url.pathname !== '/' ? url.pathname.replace(/\/$/, '').slice(0, 80) : ''}`;
  for (const [re, label] of REFERRER_CLASSES) if (re.test(host)) return label;
  return `referral:${host.replace(/^www\./, '').slice(0, 60)}`;
};

export const classifyEntry = (search: string, referrer: string | null | undefined, ownHost?: string): string => {
  let q: URLSearchParams;
  try {
    q = new URLSearchParams(search || '');
  } catch (e) {
    q = new URLSearchParams();
  }
  const source = (q.get('utm_source') || '').toLowerCase().slice(0, 40);
  const medium = (q.get('utm_medium') || '').toLowerCase();
  if (source || medium) {
    if (/email|newsletter/.test(medium) || /customer\.io|cio/.test(source)) return `email:${source || 'campaign'}`;
    if (/cpc|ppc|paid|display|retarget/.test(medium)) return `paid:${source || 'campaign'}`;
    if (/social/.test(medium)) return `social:${source || 'campaign'}`;
    return `utm:${source || medium}`;
  }
  if (q.get('gclid')) return 'paid:google';
  if (q.get('msclkid')) return 'paid:bing';
  if (q.get('fbclid') && !referrer) return 'social:facebook';
  return classifyReferrer(referrer, ownHost);
};

export const normalizePath = (pathname: string): string =>
  (pathname || '/')
    .split('/')
    .map((seg) => (/^[a-z0-9]{10,}$/i.test(seg) && /\d/.test(seg) && /[a-z]/i.test(seg) ? ':id' : seg))
    .join('/')
    .slice(0, 100) || '/';

/** The brand host's first label (apply, woo, triton …) — partner funnels differ by it. */
export const brandVariant = (): string => {
  try {
    const host = window.location.hostname.toLowerCase();
    if (host === 'localhost' || /^\d+\.\d+/.test(host)) return 'local';
    return host.split('.')[0] || 'apply';
  } catch (e) {
    return 'apply';
  }
};

export const sessionEntry = (): { flow_entry: string; flow_entry_page: string } => {
  const fallback = { flow_entry: 'direct', flow_entry_page: '/' };
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.sessionStorage.getItem(ENTRY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.flow_entry === 'string' && typeof parsed.flow_entry_page === 'string') return parsed;
    }
    const entry = {
      flow_entry: classifyEntry(window.location.search, document.referrer, window.location.hostname),
      flow_entry_page: normalizePath(window.location.pathname),
    };
    window.sessionStorage.setItem(ENTRY_KEY, JSON.stringify(entry));
    return entry;
  } catch (e) {
    return fallback;
  }
};

export const attributionParams = (): FlowParams => {
  const a = (readAttribution() || null) as Record<string, unknown> | null;
  const s = (k: string): string | undefined => {
    const v = a ? a[k] : undefined;
    return typeof v === 'string' && v !== '' ? v.slice(0, 100).toLowerCase() : undefined;
  };
  const inferredSource = a && a.fbclid ? 'facebook' : a && a.gclid ? 'google' : a && a.msclkid ? 'bing' : undefined;
  const inferredMedium = inferredSource ? 'paid_click' : undefined;
  const landingHost = s('lh');
  const landingPath = a && typeof a.lp === 'string' ? normalizePath(a.lp.split('?')[0]) : '';
  const externalRef = a && typeof a.r === 'string' ? a.r : '';
  const internalRef = a && typeof a.ir === 'string' ? a.ir : '';
  return {
    attr_source: s('utm_source') || inferredSource || NONE,
    attr_medium: s('utm_medium') || inferredMedium || NONE,
    attr_campaign: s('utm_campaign') || NONE,
    attr_content: s('utm_content') || NONE,
    attr_landing: landingHost ? `${landingHost}${landingPath === '/' ? '' : landingPath}` : NONE,
    attr_referrer: externalRef ? classifyReferrer(externalRef) : internalRef ? `tento:${internalRef}` : NONE,
  };
};

/* ------------------------------------------------------------------------ */
/* Bookkeeping                                                               */
/* ------------------------------------------------------------------------ */

const listFrom = (storage: Storage, key: string): string[] => {
  try {
    const raw = storage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const addOnce = (storage: () => Storage, key: string, value: string): boolean => {
  try {
    const st = storage();
    const list = listFrom(st, key);
    if (list.indexOf(value) !== -1) return false;
    st.setItem(key, JSON.stringify(list.slice(-199).concat(value)));
    return true;
  } catch (e) {
    return true;
  }
};

const baseParams = (flow: FlowId, step: string, extra?: FlowParams): FlowParams => ({
  flow_id: flow,
  flow_step: step,
  flow_step_index: (FLOW_STEPS[flow] as Record<string, number>)[step] ?? -1,
  flow_variant: brandVariant(),
  ...sessionEntry(),
  ...attributionParams(),
  ...extra,
});

/* ------------------------------------------------------------------------ */
/* Events                                                                    */
/* ------------------------------------------------------------------------ */

export const flowStep = (flow: FlowId, step: string, extra?: FlowParams): void => {
  try {
    if (typeof window === 'undefined') return;
    const params = baseParams(flow, step, extra);
    if (addOnce(() => window.sessionStorage, STARTED_KEY, flow)) gaEvent('flow_start', params);
    gaEvent('flow_step', params);
  } catch (e) {
    // analytics must never impact the flow
  }
};

export const flowStepComplete = (flow: FlowId, step: string, extra?: FlowParams): void => {
  try {
    gaEvent('flow_step_complete', baseParams(flow, step, extra));
  } catch (e) {
    // analytics must never impact the flow
  }
};

export const flowError = (flow: FlowId, step: string, errorType: FlowErrorType, errorCode?: string | null): void => {
  try {
    gaEvent('flow_error', {
      ...baseParams(flow, step),
      error_type: errorType,
      error_code: (errorCode || 'unknown').toString().slice(0, 100),
    });
  } catch (e) {
    // analytics must never impact the flow
  }
};

export const flowComplete = (
  flow: FlowId,
  outcome: FlowOutcome,
  step: string,
  options: { dedupeKey?: string; extra?: FlowParams } = {}
): void => {
  try {
    if (typeof window === 'undefined') return;
    if (
      options.dedupeKey &&
      !addOnce(() => window.localStorage, COMPLETED_KEY, `${flow}:${outcome}:${options.dedupeKey}`)
    )
      return;
    gaEvent('flow_complete', { ...baseParams(flow, step, options.extra), flow_outcome: outcome });
  } catch (e) {
    // analytics must never impact the flow
  }
};

/* ------------------------------------------------------------------------ */
/* Path-driven helpers used by the wizard context                             */
/* ------------------------------------------------------------------------ */

let lastViewKey = '';

/** Report a step view for a pathname; consecutive duplicates (pushState + router both firing) collapse. */
export const flowStepForPath = (pathname: string): FlowLocation | null => {
  const loc = flowForPath(pathname);
  if (!loc) {
    lastViewKey = '';
    return null;
  }
  const key = `${loc.flow}:${loc.step}`;
  if (key === lastViewKey) return loc;
  lastViewKey = key;
  flowStep(loc.flow, loc.step, loc.variant ? { flow_variant: `${brandVariant()}:${loc.variant}` } : undefined);
  return loc;
};

/** The step at `pathname` completed (its save succeeded / it navigated on). */
export const flowStepCompleteForPath = (pathname: string, extra?: FlowParams): FlowLocation | null => {
  const loc = flowForPath(pathname);
  if (!loc) return null;
  flowStepComplete(loc.flow, loc.step, {
    ...(loc.variant ? { flow_variant: `${brandVariant()}:${loc.variant}` } : {}),
    ...extra,
  });
  return loc;
};

export const flowErrorForPath = (
  pathname: string,
  errorType: FlowErrorType,
  errorCode?: string | null
): FlowLocation | null => {
  const loc = flowForPath(pathname);
  if (!loc) return null;
  flowError(loc.flow, loc.step, errorType, errorCode);
  return loc;
};

/** Test hook. */
export const __resetFlowTracking = (): void => {
  lastViewKey = '';
};
